<template>
    <div>
        <div class="email-custom-template-toolbar">
            <CompassButton
                ref="emailCustomTemplateToolBarButton"
                :label="$t.__('email.EmailCustomTemplateToolbar.label_btn_use_template')"
                :dropdown="true"
                :loading="isLoadingGetTemplates || isLoadingAddTemplateOnContentEmail"
                type="secondary"
                size="small"
                data-bot="email-custom-template-toolbar__button"
                @click="updateDropdownVisibility(!showDropdown)"
            />
            <CompassDropdown
                :key="`emailCustomTemplateToolBarDropdown-${
                    currentTemplate ? currentTemplate.id : 'none'
                }`"
                v-click-outside="handleClickOutside"
                parentRef="emailCustomTemplateToolBarButton"
                dropdownClass="email-custom-template-toolbar__dropdown"
                placement="top-end"
                :isVisible="showDropdown"
                :value="[currentTemplate ? currentTemplate.id : null]"
                :actions="[
                    {
                        label: $t.__(
                            'email.emailCustomTemplate.EmailCustomTemplateToolbar.dropdown.more_options',
                        ),
                        callback: () => {
                            showModal = true;
                            showDropdown = false;
                        },
                    },
                ]"
                :noResultsLabel="$t.__('common.labels.no_result')"
                :filterablePlaceholder="$t.__('common.actions.search')"
                filterable
            >
                <CompassDropdownOption
                    v-for="template in templates"
                    :key="template.id"
                    :label="template.name"
                    :value="template.id"
                    @click="addTemplateOnContentEmail(template.id)"
                />
            </CompassDropdown>
        </div>
        <EmailCustomTemplateModal
            :visible="showModal"
            :templates="templates"
            :selectedTemplate="selectedTemplate"
            :initCurrentRoute="initCurrentRoute"
            :isLoadingGetTemplates="isLoadingGetTemplates"
            :isLoadingSelectTemplate="isLoadingSelectTemplate"
            :isLoadingAddTemplateOnContentEmail="isLoadingAddTemplateOnContentEmail"
            @selectTemplate="selectTemplate"
            @addTemplateOnContentEmail="addTemplateOnContentEmail"
            @close="handleCloseModal"
        />
    </div>
</template>

<script>
import mime from 'mime';
import { createNamespacedHelpers } from 'vuex';
import SellsyAIRichTextMixin from 'mixins/SellsyAIRichTextMixin';
import Feature from 'Services/Feature';
import EmailCustomTemplateModal from 'vcomponents/email/emailCustomTemplate/EmailCustomTemplateModal.vue';
import {
    getListEmailTemplates,
    getEmailTemplate,
    getEmailTemplateWithContent,
} from 'api/emailEngine/emailEngineService';
import replaceHtmlEntities from 'Helpers/htmlEntities.js';
const { mapGetters: corpMapGetters } = createNamespacedHelpers('corp');

export default {
    components: {
        EmailCustomTemplateModal,
    },
    mixins: [SellsyAIRichTextMixin],
    props: {
        hashPanel: {
            type: String,
            required: true,
        },
        typePanel: {
            type: String,
            required: true,
        },
        linkedId: {
            type: [String, Number],
            default: '',
        },
        linkedType: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            showModal: false,
            isLoadingGetTemplates: false,
            isLoadingSelectTemplate: false,
            isLoadingAddTemplateOnContentEmail: false,
            templates: [],
            showDropdown: false,
            selectedTemplate: null,
            currentTemplate: null,
            currentEmailPanel: null,
            signature: null,
            initCurrentRoute: null,
            documentKeywords: [
                'estimate',
                'invoice',
                'delivery',
                'order',
                'creditnote',
                'proforma',
                'purOrder',
                'purDelivery',
                'purInvoice',
                'purCreditNote',
            ],
            excludeKeywordsForRelatedInfo: [
                'prospect',
                'client',
                'supplier',
                'thirdcontact',
                'contact',
                'company',
                'individual',
            ],
        };
    },
    computed: {
        ...corpMapGetters(['corpId', 'quotaAILimitReached']),
        isSellsyAIEnabled() {
            return Feature.isEnabled('sellsy-ai');
        },
    },
    created() {
        this.getCurrentEmailPanel();
        this.getSignature();
        this.getInitialUrlWhenPanelIsCreated();
        this.getTemplates();
    },
    methods: {
        getCurrentEmailPanel() {
            switch (this.typePanel) {
                case 'newPanel':
                    this.currentEmailPanel = this.$parent.$parent.$parent;
                    break;
                case 'consultPanel':
                    this.currentEmailPanel = this.$parent.$parent;
                    break;
                default:
                    this.currentEmailPanel = null;
            }
        },
        getSignature() {
            if (this.currentEmailPanel && this.currentEmailPanel.$props.signature) {
                this.signature = this.currentEmailPanel.$props.signature;
            }
        },
        getInitialUrlWhenPanelIsCreated() {
            const storedData = localStorage.getItem(`email_${this.corpId}_panels`);
            if (storedData) {
                const panels = JSON.parse(storedData);
                if (Array.isArray(panels) && panels.length > 0) {
                    const panel = panels.find((p) => p.id === this.hashPanel);
                    if (panel && panel.currentUrlWhenCreated) {
                        this.initCurrentRoute = JSON.parse(panel.currentUrlWhenCreated);
                    }
                }
            }
        },
        getTemplates() {
            this.isLoadingGetTemplates = true;
            getListEmailTemplates()
                .then((response) => {
                    this.templates = response.data;
                    this.templates = this.templates.sort((templateA, templateB) => {
                        return templateA.name.localeCompare(templateB.name);
                    });
                    if (this.templates.length > 0) {
                        this.selectTemplate(this.templates[0].id);
                    }
                })
                .catch(() => {
                    this.templates = [];
                })
                .finally(() => {
                    this.isLoadingGetTemplates = false;
                });
        },
        async selectTemplate(templateId) {
            if (templateId === this.selectedTemplate?.id) {
                return;
            }

            this.isLoadingSelectTemplate = true;
            await getEmailTemplate(templateId)
                .then((response) => {
                    this.selectedTemplate = response;
                    // Used for the comparison of the selected item
                    this.selectedTemplate.id = templateId;
                })
                .catch(() => {
                    this.selectedTemplate = null;
                })
                .finally(() => {
                    this.isLoadingSelectTemplate = false;
                });
        },
        async addTemplateOnContentEmail(templateId) {
            this.showDropdown = false;
            this.isLoadingAddTemplateOnContentEmail = true;

            await this.selectTemplate(templateId);

            const params = this.getContext();

            getEmailTemplateWithContent(templateId, params)
                .then((response) => {
                    const defaultAttachments = this.currentEmailPanel.attachments.filter(
                        (attachment) => !!attachment.default,
                    );
                    this.currentEmailPanel.email.subject = replaceHtmlEntities(response.subject);
                    this.currentEmailPanel.attachments = [
                        ...defaultAttachments,
                        ...this.formatAttachmentsForInsertion(),
                    ];
                    this.currentTemplate = this.selectedTemplate;
                    return this.processRelatedAI(response);
                })
                .then((newContent) => {
                    this.currentEmailPanel.email.content = newContent;
                })
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: this.$t.__('email.EmailCustomTemplateModal.insert_error'),
                    });
                })
                .finally(() => {
                    this.isLoadingAddTemplateOnContentEmail = false;
                    this.showModal = false;
                    if (this.signature) {
                        this.currentEmailPanel.richTextEditor
                            .chain()
                            .insertContent('<p></p><p></p>')
                            .insertCustomSignature({
                                signature: this.signature,
                            })
                            .run();
                    }
                    if (this.isReplyContentDisplayed) {
                        // reset action to expand reply content (because the content have been overwritten)
                        this.currentEmailPanel.expanded = true;
                    }
                    if (this.isForward) {
                        // On forward, we need to add the reply content by default
                        this.currentEmailPanel.email.content += this.getReplyContent();
                    }
                });
        },
        getContext() {
            let contextId = 0;
            let contextType = '';
            if (!!this.linkedId && !!this.linkedType) {
                contextId = Number(this.linkedId);
                contextType = this.linkedType;
            } else {
                // get the type of the contact from the url
                if (this.initCurrentRoute.fullPath.includes('opportunities')) {
                    contextId = Number(this.initCurrentRoute.params.id);
                    contextType = 'opportunity';
                } else if (this.urlContainsKeyword(this.documentKeywords)) {
                    contextId = Number(this.initCurrentRoute.query.id);
                    contextType = this.initCurrentRoute.query._f.replace(
                        /Overview|purchases_/g,
                        '',
                    );
                } else {
                    contextId = Number(this.initCurrentRoute.params.id);
                    if (this.initCurrentRoute.params.type === 'people') {
                        contextType = 'contact';
                    } else {
                        contextType = this.initCurrentRoute.params.type ?? 'contact';
                    }
                }
            }

            // in the future, related info will be used when the user can select the context
            // For now, we need to send related info with context info
            if (this.excludeKeywordsForRelatedInfo.includes(contextType)) {
                return {
                    contact: {
                        id: contextId,
                        type: contextType,
                    },
                };
            }

            return {
                // get id and type from the url params (contact, opportunity or document) for backend get the contact info
                contact: {
                    id: contextId,
                    type: contextType,
                },
                related: [
                    {
                        id: contextId,
                        type: contextType,
                    },
                ],
            };
        },
        urlContainsKeyword(keywords) {
            return keywords.some((keyword) => this.initCurrentRoute?.fullPath.includes(keyword));
        },
        formatAttachmentsForInsertion() {
            return this.selectedTemplate.attachments.map((attachment) => {
                return {
                    content_disposition: 'attachment',
                    content_id: '',
                    name: attachment.name,
                    size: attachment.size,
                    content_type: mime.getType(attachment.extension),
                    public_link: attachment.public_link,
                    extension: attachment.extension,
                };
            }, []);
        },
        processRelatedAI(response) {
            let newContent = response.content;
            if (response.related_ai && response.related_ai.length > 0) {
                return Promise.all(
                    response.related_ai.map(async (item) => {
                        let generatedResponse = '';
                        if (this.quotaAILimitReached || !this.isSellsyAIEnabled) {
                            generatedResponse = `[${item.title}]`;
                        } else {
                            generatedResponse = await this.generatePromptTagAI(item.prompt);
                        }
                        const regex = new RegExp(`(<p>)?%ai{id="${item.id}"[^}]*}ai%(</p>)?`, 'g');
                        newContent = newContent.replace(regex, generatedResponse);
                    }),
                ).then(() => newContent);
            }
            return newContent;
        },
        isReplyContentDisplayed() {
            return !this.currentEmailPanel.expanded;
        },
        isForward() {
            return this.currentEmailPanel.$props.forward;
        },
        getReplyContent() {
            return this.currentEmailPanel.$props.replyContent ?? '';
        },
        updateDropdownVisibility(visibility) {
            this.showDropdown = visibility;
        },
        handleClickOutside($event) {
            if (
                !$event ||
                (!$event.target.closest('.email-custom-template-toolbar .compass-button') &&
                    !$event.target.closest('.compass-icon-button'))
            ) {
                this.updateDropdownVisibility(false);
            }
        },
        handleCloseModal() {
            this.showModal = false;
            if (!this.currentTemplate && this.selectedTemplate.id !== this.templates[0].id) {
                this.selectTemplate(this.templates[0].id);
            } else if (
                this.currentTemplate &&
                this.currentTemplate.id !== this.selectedTemplate.id
            ) {
                this.selectTemplate(this.currentTemplate.id);
            }
        },
    },
};
</script>

<style lang="scss">
.compass-dropdown.email-custom-template-toolbar__dropdown {
    z-index: 1001;
}
</style>
